import { createContext, useContext } from 'react';

/**
 * Per-slot pub/sub for project monthly rollups. Replaces a Mantine `form.watch`
 * subscription that, in measured production traces, was firing ~432 callbacks
 * (every cell on the page) synchronously inside `setFieldValue` on every
 * keystroke — a ~187ms blocking phase before React even started rendering.
 *
 * This store is hand-rolled rather than Zustand-backed because (a) it has only
 * one consumer (the By Date editor), (b) it needs per-slot notifications so a
 * keystroke in project A doesn't wake every other project's rollup cell, and
 * (c) it integrates with `useSyncExternalStore` for React 18+ tearing safety.
 *
 * See https://react.dev/reference/react/useSyncExternalStore.
 */
class RollupStore {
  private slots = new Map<string, number | null>();
  private listeners = new Map<string, Set<() => void>>();

  /**
   * Returns the cached rollup for `key`, or null when not yet populated.
   * Stable identity across calls when the underlying value hasn't changed,
   * so `useSyncExternalStore`'s Object.is bailout can short-circuit.
   *
   * @param {string} key - Slot key produced by `rollupKey(projectId, year, month)`
   * @returns {number | null} Current rollup or null
   */
  get(key: string): number | null {
    return this.slots.get(key) ?? null;
  }

  /**
   * Updates `key`'s rollup. No-ops + skips notification when the value is
   * unchanged. Otherwise, notifies only the listeners subscribed to THIS slot
   * (not all listeners), so per-slot notifications stay O(subscribers-for-key)
   * instead of O(total-subscribers).
   *
   * @param {string} key - Slot key
   * @param {number | null} value - New rollup value
   * @returns {void}
   */
  set(key: string, value: number | null): void {
    if (this.slots.get(key) === value) return;
    this.slots.set(key, value);
    const slotListeners = this.listeners.get(key);
    if (!slotListeners) return;
    for (const listener of slotListeners) listener();
  }

  /**
   * Subscribe `listener` to changes for `key`. Returns an unsubscribe function
   * suitable for direct use as `useSyncExternalStore`'s subscribe callback.
   *
   * @param {string} key - Slot key
   * @param {() => void} listener - Called when the slot's value changes
   * @returns {() => void} Cleanup function
   */
  subscribe(key: string, listener: () => void): () => void {
    let slotListeners = this.listeners.get(key);
    if (!slotListeners) {
      slotListeners = new Set();
      this.listeners.set(key, slotListeners);
    }
    slotListeners.add(listener);
    return () => {
      const set = this.listeners.get(key);
      if (!set) return;
      set.delete(listener);
      // Drop the empty Set so a year-switch (which unmounts ~N×12 cells under
      // old keys) doesn't leave a growing trail of dead entries in the Map.
      if (set.size === 0) this.listeners.delete(key);
    };
  }
}

const RollupStoreContext = createContext<RollupStore | null>(null);

/**
 * Provider component to be placed above ProjectRollupCell consumers.
 * Wrap `useMemo(() => new RollupStore(), [])` in the parent so the store
 * instance is stable across re-renders.
 */
const RollupStoreProvider = RollupStoreContext.Provider;

/**
 * Consumer hook for the rollup store. Throws if used outside a provider so
 * mis-wires fail loudly at mount instead of silently doing nothing.
 *
 * @returns {RollupStore} The store instance from the nearest provider
 * @throws {Error} When called outside a RollupStoreProvider
 */
function useRollupStore(): RollupStore {
  const store = useContext(RollupStoreContext);
  if (store === null) {
    throw new Error('useRollupStore must be used inside RollupStoreProvider');
  }
  return store;
}

/**
 * Builds the canonical slot key for a (projectId, year, month) tuple. The year
 * is included so a year-switch doesn't accidentally surface a previous year's
 * cached rollup until the bulk-populate effect re-runs. Centralized so the
 * producer (cell onChange) and consumer (ProjectRollupCell) cannot drift.
 *
 * @param {string} projectId - Project identifier
 * @param {number} year - Active year (matches `selectedYear` from the financials store)
 * @param {number} month - 1-indexed month (1 = January, 12 = December)
 * @returns {string} The slot key
 */
function rollupKey(projectId: string, year: number, month: number): string {
  return `${projectId}:${year}:${month}`;
}

export { RollupStore, RollupStoreProvider, rollupKey, useRollupStore };
